import React, { useState, useRef, useEffect, useCallback } from 'react';
import { Mic, Square, Play, Save, Loader2, AlertCircle, RotateCcw } from 'lucide-react';
import Button from '../../common/Button';

interface VoiceRecorderProps {
  onSave: (audioBlob: Blob) => Promise<void>;
  maxDuration?: number;
  disabled?: boolean;
}

export default function VoiceRecorder({
  onSave,
  maxDuration = 60,
  disabled = false
}: VoiceRecorderProps) {
  const [isRecording, setIsRecording] = useState(false);
  const [audioBlob, setAudioBlob] = useState<Blob | null>(null);
  const [audioUrl, setAudioUrl] = useState<string | null>(null);
  const [recordingTime, setRecordingTime] = useState(0);
  const [isPlaying, setIsPlaying] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [permissionDenied, setPermissionDenied] = useState(false);
  const [isSupported, setIsSupported] = useState(true);

  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const audioChunksRef = useRef<Blob[]>([]);
  const timerRef = useRef<NodeJS.Timeout | null>(null);
  const audioPlayerRef = useRef<HTMLAudioElement | null>(null);
  const streamRef = useRef<MediaStream | null>(null);

  useEffect(() => {
    if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
      setIsSupported(false);
      setError('Voice recording is not supported in your browser. Please use a modern browser like Chrome, Firefox, or Safari.');
    }

    return () => {
      cleanup();
    };
  }, []);

  const cleanup = useCallback(() => {
    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }

    if (streamRef.current) {
      streamRef.current.getTracks().forEach(track => track.stop());
      streamRef.current = null;
    }

    if (audioPlayerRef.current) {
      audioPlayerRef.current.pause();
      audioPlayerRef.current = null;
    }

    if (audioUrl) {
      URL.revokeObjectURL(audioUrl);
    }
  }, [audioUrl]);

  const startRecording = async () => {
    if (disabled || isSaving || !isSupported) return;

    try {
      setError(null);
      setPermissionDenied(false);

      const stream = await navigator.mediaDevices.getUserMedia({
        audio: {
          echoCancellation: true,
          noiseSuppression: true,
          sampleRate: 44100
        }
      });

      streamRef.current = stream;

      let mimeType = 'audio/webm';
      if (MediaRecorder.isTypeSupported('audio/webm;codecs=opus')) {
        mimeType = 'audio/webm;codecs=opus';
      } else if (MediaRecorder.isTypeSupported('audio/mp4')) {
        mimeType = 'audio/mp4';
      } else if (MediaRecorder.isTypeSupported('audio/ogg;codecs=opus')) {
        mimeType = 'audio/ogg;codecs=opus';
      }

      const mediaRecorder = new MediaRecorder(stream, {
        mimeType,
        audioBitsPerSecond: 128000
      });

      mediaRecorderRef.current = mediaRecorder;
      audioChunksRef.current = [];

      mediaRecorder.ondataavailable = (event) => {
        if (event.data && event.data.size > 0) {
          audioChunksRef.current.push(event.data);
        }
      };

      mediaRecorder.onstop = () => {
        if (audioChunksRef.current.length === 0) {
          setError('Recording failed. No audio data captured.');
          return;
        }

        const audioBlob = new Blob(audioChunksRef.current, { type: mimeType });

        if (audioBlob.size === 0) {
          setError('Recording failed. Audio file is empty.');
          return;
        }

        setAudioBlob(audioBlob);
        const url = URL.createObjectURL(audioBlob);
        setAudioUrl(url);

        if (streamRef.current) {
          streamRef.current.getTracks().forEach(track => track.stop());
          streamRef.current = null;
        }
      };

      mediaRecorder.onerror = (event) => {
        console.error('MediaRecorder error:', event);
        setError('Recording error occurred. Please try again.');
        stopRecording();
      };

      mediaRecorder.start(100);
      setIsRecording(true);
      setRecordingTime(0);

      timerRef.current = setInterval(() => {
        setRecordingTime(prev => {
          const newTime = prev + 1;
          if (newTime >= maxDuration) {
            stopRecording();
            return maxDuration;
          }
          return newTime;
        });
      }, 1000);

    } catch (err) {
      const error = err as Error;
      console.error('Error accessing microphone:', err);

      if (error.name === 'NotAllowedError' || error.name === 'PermissionDeniedError') {
        setPermissionDenied(true);
        setError('Microphone access denied. Please allow microphone access in your browser settings.');
      } else if (error.name === 'NotFoundError') {
        setError('No microphone found. Please connect a microphone and try again.');
      } else if (error.name === 'NotReadableError') {
        setError('Microphone is already in use by another application.');
      } else {
        setError('Failed to access microphone. Please check your browser permissions and try again.');
      }

      if (streamRef.current) {
        streamRef.current.getTracks().forEach(track => track.stop());
        streamRef.current = null;
      }
    }
  };

  const stopRecording = useCallback(() => {
    if (mediaRecorderRef.current && isRecording) {
      if (mediaRecorderRef.current.state === 'recording') {
        mediaRecorderRef.current.stop();
      }
      setIsRecording(false);

      if (timerRef.current) {
        clearInterval(timerRef.current);
        timerRef.current = null;
      }
    }
  }, [isRecording]);

  const playRecording = () => {
    if (!audioUrl || isPlaying) return;

    try {
      const audio = new Audio(audioUrl);
      audioPlayerRef.current = audio;

      audio.onended = () => {
        setIsPlaying(false);
        audioPlayerRef.current = null;
      };

      audio.onerror = () => {
        setIsPlaying(false);
        setError('Failed to play recording. The audio file may be corrupted.');
        audioPlayerRef.current = null;
      };

      audio.play().catch(err => {
        console.error('Error playing audio:', err);
        setError('Failed to play recording. Please try recording again.');
        setIsPlaying(false);
      });

      setIsPlaying(true);
    } catch (err) {
      console.error('Error creating audio player:', err);
      setError('Failed to play recording. Please try recording again.');
    }
  };

  const handleSave = async () => {
    if (!audioBlob || isSaving) return;

    if (audioBlob.size === 0) {
      setError('Cannot save empty recording. Please record again.');
      return;
    }

    if (audioBlob.size > 10 * 1024 * 1024) {
      setError('Recording is too large (max 10MB). Please record a shorter message.');
      return;
    }

    setIsSaving(true);
    setError(null);

    try {
      await onSave(audioBlob);

      setAudioBlob(null);
      setAudioUrl(null);
      setRecordingTime(0);

      cleanup();
    } catch (err) {
      console.error('Error saving recording:', err);
      const errorMessage = (err as Error).message || 'Failed to save recording. Please try again.';
      setError(errorMessage);
    } finally {
      setIsSaving(false);
    }
  };

  const handleReset = () => {
    if (isSaving || isRecording) return;

    if (audioPlayerRef.current) {
      audioPlayerRef.current.pause();
    }

    setAudioBlob(null);
    setAudioUrl(null);
    setRecordingTime(0);
    setIsPlaying(false);
    setError(null);

    cleanup();
  };

  const formatTime = (seconds: number): string => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  if (!isSupported) {
    return (
      <div className="bg-yellow-50 border border-yellow-200 rounded-md p-3 text-sm text-yellow-800">
        <p className="font-medium mb-1">Voice Recording Not Supported</p>
        <p>Your browser does not support voice recording. Please use a modern browser like Chrome, Firefox, Edge, or Safari.</p>
      </div>
    );
  }

  return (
    <div className="space-y-3" role="region" aria-label="Voice recording interface">
      {error && (
        <div
          className="bg-red-50 border border-red-200 rounded-md p-3 text-sm text-red-800 flex items-start gap-2"
          role="alert"
          aria-live="assertive"
        >
          <AlertCircle className="w-4 h-4 flex-shrink-0 mt-0.5" aria-hidden="true" />
          <span>{error}</span>
        </div>
      )}

      {permissionDenied && !error && (
        <div
          className="bg-yellow-50 border border-yellow-200 rounded-md p-3 text-sm text-yellow-800"
          role="alert"
          aria-live="polite"
        >
          <p className="font-medium mb-1">Microphone Permission Required</p>
          <p>To record your voice introduction, allow microphone access in your browser settings.</p>
        </div>
      )}

      <div className="flex items-center gap-3 flex-wrap">
        {!isRecording && !audioBlob && (
          <Button
            type="button"
            variant="primary"
            size="sm"
            onClick={startRecording}
            icon={Mic}
            disabled={disabled || isSaving}
            aria-label="Start recording voice introduction"
          >
            Start Recording
          </Button>
        )}

        {isRecording && (
          <>
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={stopRecording}
              icon={Square}
              className="border-red-500 text-red-600 hover:bg-red-50"
              aria-label={`Stop recording. Time elapsed: ${formatTime(recordingTime)}`}
            >
              Stop Recording
            </Button>
            <div className="flex items-center gap-2" role="timer" aria-live="polite">
              <div
                className="w-2 h-2 bg-red-500 rounded-full animate-pulse"
                aria-hidden="true"
              />
              <span className="text-sm font-medium text-gray-700">
                {formatTime(recordingTime)} / {formatTime(maxDuration)}
              </span>
            </div>
          </>
        )}

        {audioBlob && !isRecording && (
          <>
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={playRecording}
              disabled={isPlaying || isSaving}
              icon={Play}
              aria-label={isPlaying ? 'Playing recording' : 'Play recording'}
            >
              {isPlaying ? 'Playing...' : 'Play Recording'}
            </Button>
            <Button
              type="button"
              variant="primary"
              size="sm"
              onClick={handleSave}
              disabled={isSaving}
              icon={isSaving ? Loader2 : Save}
              className={isSaving ? 'opacity-75' : ''}
              aria-label={isSaving ? 'Uploading recording' : 'Save recording'}
              aria-busy={isSaving}
            >
              {isSaving ? 'Uploading...' : 'Save Recording'}
            </Button>
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={handleReset}
              disabled={isSaving}
              icon={RotateCcw}
              aria-label="Discard recording and start over"
            >
              Reset
            </Button>
            <span className="text-sm text-gray-600" aria-live="polite">
              Duration: {formatTime(recordingTime)}
            </span>
          </>
        )}
      </div>

      <p className="text-xs text-gray-500">
        Record a brief voice introduction (max {maxDuration} seconds). Speak clearly and introduce yourself, your experience, and what you're looking for.
      </p>
    </div>
  );
}
